import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { NAV_ITEMS } from '../utils/constants'
import './Header.css'

// Helper function để lấy icon cho từng nav item
const getIconForNavItem = (label: string): string => {
  switch (label) {
    case 'Trang chủ': return 'fa-home'
    case 'Công trình thiết kế': return 'fa-drafting-compass'
    case 'Công trình thi công': return 'fa-hard-hat'
    case 'Nội thất': return 'fa-couch'
    case 'Giới thiệu': return 'fa-info-circle'
    case 'Liên hệ': return 'fa-phone-alt'
    default: return 'fa-circle'
  }
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeLanguage, setActiveLanguage] = useState<'en' | 'vi'>('vi')
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const [activeSubDropdown, setActiveSubDropdown] = useState<string | null>(null)
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState<string | null>(null)
  const [mobileActiveSubDropdown, setMobileActiveSubDropdown] = useState<string | null>(null)
  const [hoverTimeout, setHoverTimeout] = useState<number | null>(null)
  const location = useLocation()
  const navigate = useNavigate()

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = searchQuery.trim()
    if (trimmed) {
      navigate(`/cong-trinh-thiet-ke?q=${encodeURIComponent(trimmed)}`)
    } else {
      navigate('/cong-trinh-thiet-ke')
    }
    setIsMobileSearchOpen(false)
    setIsMobileMenuOpen(false)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    if (isMobileSearchOpen) {
      setIsMobileSearchOpen(false)
    }
    // Update header height sau khi toggle menu (để tính lại chiều cao)
    setTimeout(() => {
      const header = document.querySelector('.header') as HTMLElement
      if (header) {
        const headerHeight = header.offsetHeight
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`)
      }
    }, 100) // Delay nhỏ để DOM update xong
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
    setMobileActiveDropdown(null)
    setMobileActiveSubDropdown(null)
    // Update header height sau khi đóng menu
    setTimeout(() => {
      const header = document.querySelector('.header') as HTMLElement
      if (header) {
        const headerHeight = header.offsetHeight
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`)
      }
    }, 100)
  }

  const handleDropdownToggle = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label)
  }

  const handleDropdownClose = () => {
    setActiveDropdown(null)
  }

  // Hover delay functions
  const handleDropdownEnter = (label: string) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      setHoverTimeout(null)
    }
    setActiveDropdown(label)
  }

  const handleDropdownLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null)
      setActiveSubDropdown(null)
    }, 500) // 500ms delay - rất nhiều thời gian di chuyển chuột
    setHoverTimeout(timeout as unknown as number)
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  // Function để check active state
  const isActiveRoute = (path: string) => {
    const currentPath = location.pathname
    
    // Trang chủ chỉ active khi đúng '/'
    if (path === '/') {
      return currentPath === '/'
    }
    
    // Các trang khác active khi exact match
    return currentPath === path
  }

  // Đóng tất cả dropdowns khi chuyển trang (route change)
  useEffect(() => {
    setActiveDropdown(null)
    setActiveSubDropdown(null)
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Đồng bộ search input với query parameter trên URL
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const q = params.get('q') || ''
    setSearchQuery(q)
  }, [location.search])

  useEffect(() => {
    // Tính chiều cao header và set vào CSS variable
    const updateHeaderHeight = () => {
      const header = document.querySelector('.header') as HTMLElement
      if (header) {
        const headerHeight = header.offsetHeight
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`)
      }
    }

    // Gọi ngay khi mount
    updateHeaderHeight()

    // Gọi lại khi DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateHeaderHeight)
    }

    // Gọi lại khi window load
    window.addEventListener('load', updateHeaderHeight)

    // Gọi lại khi resize window
    window.addEventListener('resize', updateHeaderHeight)

    // Dùng ResizeObserver để theo dõi thay đổi chiều cao header (khi menu mở/đóng, search expand...)
    const headerEl = document.querySelector('.header') as HTMLElement
    let resizeObserver: ResizeObserver | null = null
    
    if (headerEl && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight()
      })
      resizeObserver.observe(headerEl)
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY
      
      // Hiện/ẩn header dựa trên hướng scroll
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scroll xuống - ẩn header
        setIsHeaderVisible(false)
        setIsMobileMenuOpen(false) // Đóng mobile menu khi scroll
        setActiveDropdown(null) // Đóng desktop dropdown khi scroll
        setActiveSubDropdown(null)
      } else if (currentScrollY < lastScrollY || currentScrollY <= 100) {
        // Scroll lên hoặc ở đầu trang - hiện header
        setIsHeaderVisible(true)
      }
      
      // Hiện/ẩn back to top button
      setShowBackToTop(currentScrollY > 300)
      
      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', updateHeaderHeight)
      window.removeEventListener('load', updateHeaderHeight)
      document.removeEventListener('DOMContentLoaded', updateHeaderHeight)
      // Cleanup ResizeObserver
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      // Cleanup timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout)
      }
    }
  }, [lastScrollY, hoverTimeout])

  return (
    <>
    <header className={`header ${isHeaderVisible ? 'header-visible' : 'header-hidden'}`}>
      {/* Thanh trên: Logo + Search + Phone numbers */}
      <div className="header-top">
        <div className="container">
          <div className="header-top-content">
            {/* Mobile Hamburger Menu Button (Left on Mobile) */}
            <button 
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>

            {/* Logo */}
            <Link 
              to="/" 
              className="logo"
              onClick={() => {
                closeMobileMenu();
                setIsMobileSearchOpen(false);
              }}
            >
              <img src="/assets/logo/logo_header.png" alt="MAXCONST Logo" />
            </Link>

            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="search-container">
              <div className="search-wrapper">
                <input 
                  type="text" 
                  placeholder="Tìm kiếm..." 
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-button">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>

            {/* Phone Numbers */}
            <div className="phone-numbers">
              <div className="phone-item">
                <i className="fas fa-phone"></i>
                <div className="phone-info">
                  <span className="phone-label">24/7</span>
                  <a href="tel:0979808278" className="phone-number">0979 808 278</a>
                </div>
              </div>
              <div className="phone-item">
                <i className="fas fa-phone"></i>
                <div className="phone-info">
                  <span className="phone-label">Hotline</span>
                  <a href="tel:0979808278" className="phone-number">0979 808 278</a>
                </div>
              </div>
            </div>

            {/* Mobile Search Toggle Button (Right on Mobile) */}
            <button 
              className="mobile-search-toggle"
              onClick={() => {
                setIsMobileSearchOpen(!isMobileSearchOpen);
                if (isMobileMenuOpen) setIsMobileMenuOpen(false);
              }}
              aria-label="Toggle mobile search"
            >
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Overlay Bar */}
      {isMobileSearchOpen && (
        <div className="mobile-search-overlay-bar">
          <div className="container">
            <form onSubmit={handleSearchSubmit} className="mobile-search-form">
              <input 
                type="text" 
                placeholder="Bạn muốn tìm gì ?" 
                className="mobile-search-input"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button 
                type="submit" 
                className="mobile-search-submit-btn" 
              >
                <i className="fas fa-search"></i>
              </button>
              <button 
                type="button" 
                className="mobile-search-close-btn"
                onClick={() => setIsMobileSearchOpen(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Debug UI removed: test navigation and debug widgets were here */}
      
             {/* Thanh dưới: Menu chức năng */}
       <div className="header-bottom">
         <div className="container">
           <div className="nav-container">
             {/* Desktop Navigation */}
             <nav className="desktop-nav">
               {NAV_ITEMS.map((item, index) => {
                 return (
                   <div key={index} className="nav-item">
                     {item.submenu ? (
                       <div 
                         className="dropdown-container"
                         onMouseEnter={() => handleDropdownEnter(item.label)}
                         onMouseLeave={handleDropdownLeave}
                       >
                         <Link 
                           to={item.path}
                           className={`nav-link dropdown-toggle ${isActiveRoute(item.path) ? 'active' : ''}`}
                           onClick={(e) => {
                             // Trên desktop, cho phép click để navigate
                             // Nhưng nếu là touch device (không hover), click lần đầu sẽ mở dropdown
                             if (window.matchMedia('(max-width: 1024px)').matches) {
                               e.preventDefault()
                               handleDropdownToggle(item.label)
                             }
                           }}
                         >
                           <i className={`fas ${getIconForNavItem(item.label)}`}></i>
                           {item.label}
                           <i className="fas fa-chevron-down"></i>
                         </Link>
                         
                        {activeDropdown === item.label && (
                          <ul className="dropdown-menu">
                            {item.submenu.map((subItem: any, subIndex: number) => (
                              <li 
                                key={subIndex} 
                                className={`dropdown-item-wrapper ${subItem.submenu ? 'has-submenu' : ''}`}
                                onMouseEnter={() => subItem.submenu && setActiveSubDropdown(subItem.label)}
                                onMouseLeave={() => subItem.submenu && setActiveSubDropdown(null)}
                              >
                                <Link
                                  to={subItem.path}
                                  className="dropdown-item"
                                  onClick={handleDropdownClose}
                                >
                                  {subItem.label}
                                  {subItem.submenu && <i className="fas fa-chevron-right submenu-arrow"></i>}
                                </Link>
                                
                                {/* Submenu cấp 2 */}
                                {subItem.submenu && activeSubDropdown === subItem.label && (
                                  <ul className="dropdown-menu dropdown-submenu">
                                    {subItem.submenu.map((subSubItem: any, subSubIndex: number) => (
                                      <li key={subSubIndex}>
                                        <Link
                                          to={subSubItem.path}
                                          className="dropdown-item"
                                          onClick={handleDropdownClose}
                                        >
                                          {subSubItem.label}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                       </div>
                     ) : (
                       <Link 
                         to={item.path} 
                         className={`nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                       >
                         <i className={`fas ${getIconForNavItem(item.label)}`}></i>
                         {item.label}
                       </Link>
                     )}
                   </div>
                 )
               })}
             </nav>
             
           </div>
         </div>
       </div>

        <nav className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-overlay" onClick={closeMobileMenu}></div>
          <div className="mobile-nav-content">
            {/* Close Button */}
            <button className="mobile-nav-close" onClick={closeMobileMenu} aria-label="Close menu">
              <i className="fas fa-times"></i>
            </button>
            
            {/* Mobile Search Bar */}
            <form onSubmit={handleSearchSubmit} className="mobile-search-container">
              <div className="search-wrapper">
                <input 
                  type="text" 
                  placeholder="Bạn muốn tìm gì ?" 
                  className="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-button">
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </form>

            {/* Mobile Language Selector */}
            <div className="mobile-lang-selector">
              <button 
                className={`mobile-lang-btn ${activeLanguage === 'en' ? 'active' : ''}`} 
                onClick={() => setActiveLanguage('en')}
                aria-label="English"
              >
                <img src="https://flagcdn.com/w40/gb.png" alt="English" />
              </button>
              <button 
                className={`mobile-lang-btn ${activeLanguage === 'vi' ? 'active' : ''}`} 
                onClick={() => setActiveLanguage('vi')}
                aria-label="Tiếng Việt"
              >
                <img src="https://flagcdn.com/w40/vn.png" alt="Tiếng Việt" />
              </button>
            </div>

            <div className="mobile-menu-list">
              {NAV_ITEMS.map((item, index) => {
                const hasSubmenu = !!item.submenu;
                const isDropdownOpen = mobileActiveDropdown === item.label;
                
                return (
                  <div key={index} className="mobile-nav-item">
                    <div className="mobile-nav-row">
                      <Link 
                        to={item.path} 
                        className={`mobile-nav-link ${isActiveRoute(item.path) ? 'active' : ''}`} 
                        onClick={() => {
                          closeMobileMenu();
                        }}
                      >
                        <span>{item.label}</span>
                      </Link>
                      {hasSubmenu && (
                        <button 
                          className={`mobile-toggle-btn ${isDropdownOpen ? 'open' : ''}`}
                          onClick={() => setMobileActiveDropdown(isDropdownOpen ? null : item.label)}
                          aria-label="Toggle submenu"
                        >
                          <i className="fas fa-chevron-down"></i>
                        </button>
                      )}
                    </div>
                    
                    {/* Submenu cấp 1 trên mobile */}
                    {hasSubmenu && isDropdownOpen && (
                      <div className="mobile-submenu">
                        {item.submenu.map((subItem: any, subIndex: number) => {
                          const hasSubSubmenu = !!subItem.submenu;
                          const isSubSubmenuOpen = mobileActiveSubDropdown === subItem.label;
                          
                          return (
                            <div key={subIndex} className="mobile-submenu-item-wrap">
                              <div className="mobile-nav-row mobile-submenu-row">
                                <Link
                                  to={subItem.path}
                                  className="mobile-submenu-link"
                                  onClick={() => {
                                    closeMobileMenu();
                                  }}
                                >
                                  {subItem.label}
                                </Link>
                                {hasSubSubmenu && (
                                  <button 
                                    className={`mobile-toggle-btn sub-toggle-btn ${isSubSubmenuOpen ? 'open' : ''}`}
                                    onClick={() => setMobileActiveSubDropdown(isSubSubmenuOpen ? null : subItem.label)}
                                    aria-label="Toggle sub-submenu"
                                  >
                                    <i className="fas fa-chevron-down"></i>
                                  </button>
                                )}
                              </div>
                              
                              {/* Submenu cấp 2 trên mobile */}
                              {hasSubSubmenu && isSubSubmenuOpen && (
                                <div className="mobile-sub-submenu">
                                  {subItem.submenu.map((subSubItem: any, subSubIndex: number) => (
                                    <Link
                                      key={subSubIndex}
                                      to={subSubItem.path}
                                      className="mobile-sub-submenu-link"
                                      onClick={closeMobileMenu}
                                    >
                                      {subSubItem.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Mobile Social Links */}
            <div className="mobile-social-links">
              <a href="https://facebook.com" target="_blank" rel="noreferrer" className="mobile-social-icon facebook" aria-label="Facebook">
                <i className="fab fa-facebook-f"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="mobile-social-icon instagram" aria-label="Instagram">
                <i className="fab fa-instagram"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" className="mobile-social-icon twitter" aria-label="Twitter">
                <i className="fab fa-twitter"></i>
              </a>
              <a href="mailto:vpmaxconst@gmail.com" className="mobile-social-icon mail" aria-label="Mail">
                <i className="fas fa-envelope"></i>
              </a>
            </div>
          </div>
        </nav>
    </header>
    
    {/* Back to Top Button */}
    {showBackToTop && (
      <button 
        className="back-to-top"
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <i className="fas fa-chevron-up"></i>
      </button>
    )}
    </>
  )
}

export default Header
